import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import type { ReactElement } from 'react'

import { CatalogTable, catalogListQuery } from '~/features/addons'
import type { AppError } from '~/lib/result'

/**
 * `/admin/subscriptions/addons` — catalog management list.
 *
 * Shows every `addon_catalog` row (active + inactive) so platform admins can
 * see the full product catalog at a glance. Family-tier rows are visually
 * grouped under their family heading (e.g. recruitment_basic/pro/intelligent).
 *
 * "New Add-on" routes to the detail page with the sentinel `new` key.
 *
 * Permission-denied state renders inline rather than throwing so the
 * AdminShell chrome stays consistent — matches the pattern used by the
 * Plans list route.
 */
export const Route = createFileRoute('/admin/subscriptions/addons/')({
  component: AddonsListPage,
})

function AddonsListPage(): ReactElement {
  const catalog = useQuery(catalogListQuery())

  const apiError = catalog.error as AppError | null
  const isPermissionDenied =
    apiError?.code === 'forbidden' || apiError?.code === 'unauthorized'

  if (isPermissionDenied) {
    return (
      <div className="ka-card">
        <div className="ka-empty">
          <div className="ka-empty-title">Restricted</div>
          <div className="ka-empty-body">
            Request <code>subscription_management</code> from a super-admin.
          </div>
        </div>
      </div>
    )
  }

  const rows = catalog.data?.catalog ?? []
  const totalActive = rows.filter((r) => r.is_active).length
  const totalGa = rows.filter((r) => r.status === 'ga').length
  const totalEarlyAccess = rows.filter((r) => r.status === 'early_access').length
  const totalComingSoon = rows.filter((r) => r.status === 'coming_soon').length

  return (
    <div className="ka-subs-list-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Add-ons</h1>
          <p className="ka-page-header-subtitle">
            {totalActive} active · {totalGa} GA · {totalEarlyAccess} early-access ·{' '}
            {totalComingSoon} coming soon
          </p>
        </div>
        <div className="ka-page-header-actions">
          <Link
            to="/admin/subscriptions/addons/$addonKey"
            params={{ addonKey: 'new' }}
            className="ka-btn ka-btn--primary"
          >
            <Plus size={14} aria-hidden="true" /> New Add-on
          </Link>
        </div>
      </header>

      <CatalogTable catalog={rows} isLoading={catalog.isPending} />
    </div>
  )
}
